"use client";

import { useAnimation } from "@/lib/animation-context";

export function BackgroundPattern() {
  const { bgAnimation } = useAnimation();

  // Determine grid line color based on animation state
  const getGridColor = () => {
    if (bgAnimation === "scanning") return "rgba(34, 197, 94, 0.3)"; // Green for scanning
    if (bgAnimation === "phishing") return "rgba(220, 38, 38, 0.3)"; // Dark red for phishing
    return "rgba(37, 99, 235, 0.3)"; // Bluish (matching X gradient)
  };

  const gridColor = getGridColor();

  return (
    <>
      {/* Solid dark blue background matching navbar/footer colors */}
      <div 
        className="fixed -z-20 pointer-events-none bg-linear-to-r from-slate-950 via-slate-900 to-slate-950"
        style={{
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          opacity: 0.7
        }} 
      />

      <style>{`
        @keyframes grid-flow-left {
          0% { background-position: 0 0; }
          100% { background-position: 15px 15px; }
        }
        
        @keyframes grid-flow-right {
          0% { background-position: 0 0; }
          100% { background-position: -15px -15px; }
        }
        
        .grid-animate-left {
          animation: grid-flow-left 20s linear infinite;
        }
        
        .grid-animate-right {
          animation: grid-flow-right 20s linear infinite;
        }
      `}</style>

      {/* Left side repeating grid pattern */}
      <div 
        className="fixed -z-10 pointer-events-none grid-animate-left"
        style={{ 
          left: 0, 
          top: 0, 
          width: '33.333%', 
          height: '100%',
          minHeight: '100vh',
          backgroundImage: `
            linear-gradient(0deg, ${gridColor} 1px, transparent 1px),
            linear-gradient(90deg, ${gridColor} 1px, transparent 1px)
          `,
          backgroundSize: '15px 15px',
          backgroundRepeat: 'repeat',
          backgroundAttachment: 'fixed',
          opacity: 0.15,
          WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%)',
          maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%)',
          transition: 'opacity 0.5s ease'
        }}
      />

      {/* Right side repeating grid pattern */}
      <div 
        className="fixed -z-10 pointer-events-none grid-animate-right"
        style={{ 
          right: 0, 
          top: 0, 
          width: '33.333%', 
          height: '100%',
          minHeight: '100vh',
          backgroundImage: `
            linear-gradient(0deg, ${gridColor} 1px, transparent 1px),
            linear-gradient(90deg, ${gridColor} 1px, transparent 1px)
          `,
          backgroundSize: '15px 15px',
          backgroundRepeat: 'repeat',
          backgroundAttachment: 'fixed',
          opacity: 0.15,
          WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%)',
          maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%)',
          transition: 'opacity 0.5s ease'
        }}
      />

      {/* Left blur fade overlay */}
      <div 
        className="fixed -z-10 pointer-events-none"
        style={{
          left: 0,
          top: 0,
          width: '30%',
          height: '100%',
          minHeight: '100vh',
          background: 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%)',
          backdropFilter: 'blur(1px)',
          opacity: 0.3
        }} 
      />

      {/* Right blur fade overlay */}
      <div 
        className="fixed -z-10 pointer-events-none"
        style={{
          right: 0,
          top: 0,
          width: '30%',
          height: '100%',
          minHeight: '100vh',
          background: 'linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%)',
          backdropFilter: 'blur(1px)',
          opacity: 0.3
        }} 
      />

      {/* Center gradient background (cyan to blue) - Degraded pixelated effect */}
      <div 
        className="fixed -z-10 pointer-events-none"
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          background: `
            radial-gradient(ellipse 800px 600px at center, 
              rgba(6, 182, 212, 0.08) 0%, 
              rgba(59, 130, 246, 0.05) 35%, 
              rgba(37, 99, 235, 0.02) 100%
            )
          `,
          opacity: 0.4,
          filter: 'pixelate(8px) contrast(0.9)',
          imageRendering: 'pixelated'
        }} 
      />
    </>
  );
}
